<script setup lang="ts">
import { IconComp } from '@/components';
import { featureIcons } from '@/.';
import { computed, onMounted, ref } from 'vue';
import ButtonComp from '../Basic/ButtonComp.vue';
import { useDismissedMessages, useDismissedMessagesRef } from '@/composeables/messages/dismissedMessages';
import { Alert } from 'bootstrap';

const props = withDefaults(
    defineProps<{
        /**
         * The type of the alert.
         * @default 'warning'
         */
        type?: 'success' | 'info' | 'warning' | 'danger' | 'tip' | 'none';

        /**
         * Whether to show the icon or not.
         * @default true
         */
        showIcon?: boolean;

        /**
         * The title of the alert. This is the header of the alert.
         * Should be a translation key.
         */
        title?: string;

        /**
         * Dismisskey to close the alert. This should be a unique key to identify the alert.
         * @default undefined
         */
        dismisskey?: string;

        /**
         * Whether to dismiss the alert forever or not.
         * @default false
         */
        dismissforever?: boolean;

        /**
         * Wheter the message is pr event or global.
         * This is only in use if the dismisskey is set.
         * @default false
         */
        globalmessage?: boolean;

        /**
         * The size of the alert.
         * This is basically the amount of padding the alert has.
         * @default 'md'
         */
        size?: 'xs' | 'sm' | 'md';

        /**
         * The class to apply to the alert.
         */
        fulldismissButton?: boolean;

        /**
         * Whether the alert is dismissable or not.
         * @default false
         */
        dismissable?: boolean;

        /**
         * The auto dismiss period in seconds. How long before the alert is dismissed.
         * @default undefined
         */
        autoDismissPeriod?: number;

        /**
         * Contains classes that will be added to the content of the slot
         */
        innerclass?: string;
    }>(),
    {
        type: 'warning',
        showIcon: true,
        size: 'md',
        fulldismissButton: false,
        dismissforever: false,
        globalmessage: false,
        dissmissable: false,
    },
);

let alertInstance: Alert | null = null;

const alert = ref<HTMLDivElement | null>(null);

// Get the dismissed messages hash as a ref
const { DismissedMessages } = useDismissedMessagesRef();

// The calculated dismisskey hash. This is based on the KnuthHash algorithm
const dismisskeyvalue = ref<string | undefined>(undefined);

/**
 * Get the icon for the alert
 */
const getIcon = computed(() => {
    switch (props.type) {
        case 'success':
            return featureIcons.SUCCESS;
        case 'info':
            return featureIcons.INFO;
        case 'danger':
            return featureIcons.ERROR;
        case 'tip':
            return featureIcons.TIP;
        default:
            return featureIcons.WARNING;
    }
});

/**
 * Dismiss the message
 * If the dismisskey is a unique key, we want to dismiss the message, but only for a certain amount of time 7 days.
 * If the dismissforever is true then we want to dismiss the message "forever" i.e 60 days.
 */
const onDismissed = () => {
    isDismissed.value = true;
    if (dismisskeyvalue.value === undefined) {
        emits('close');
        return;
    }

    let dismissForDays = props.dismissforever ? 24 * 60 : 24 * 7; // We dismiss the message for 60 days if we dont have a dismisskey or 7 days if we do

    useDismissedMessages().Dismiss(dismisskeyvalue.value, dismissForDays, props.globalmessage);

    emits('close');
};

const isDismissed = ref(false);

/**
 * We set up a timer to dismiss the message after a certain amount of time
 */
if (props.autoDismissPeriod && props.autoDismissPeriod > 0) {
    setTimeout(() => {
        alertInstance?.close();
    }, props.autoDismissPeriod * 1000);
}

const isVisibleComputed = computed(() => {
    if (isDismissed.value) {
        return false;
    }

    if (DismissedMessages.value !== null && DismissedMessages.value.length == 0) {
        return true;
    }

    if (DismissedMessages.value !== null && dismisskeyvalue.value != undefined && DismissedMessages.value.includes(dismisskeyvalue.value)) {
        return false;
    }

    return (
        props.dismisskey === undefined ||
        (DismissedMessages.value !== null && dismisskeyvalue.value != undefined && !DismissedMessages.value.includes(dismisskeyvalue.value))
    );
});

onMounted(async () => {
    if (!alertInstance) {
        alertInstance = Alert.getOrCreateInstance(alert.value!);
    }

    if (props.dismisskey && props.dismisskey.length > 0) {
        dismisskeyvalue.value = props.dismisskey ?? '';
    }
});

const emits = defineEmits<{ (e: 'close'): void }>();
</script>
<template>
    <div v-if="$slots.default && isVisibleComputed">
        <div
            ref="alert"
            class="d-flex alert mb-0"
            :class="{
                [`alert-${props.type}`]: true,
                'bg-opacity-50': true,
                'alert-dismissible fade show': props.dismissable || (props.dismisskey && props.dismisskey.length > 0),
                'px-2 py-1': props.size === 'sm' || props.size === 'xs',
                'px-0': props.type === 'none',
                small: props.size === 'sm' || props.size === 'xs',
                'border-0': props.size === 'xs',
            }"
            role="alert"
            v-on="{ 'closed.bs.alert': onDismissed }"
        >
            <div
                class="alert-icon-container align-self-start ms-2"
                v-if="showIcon"
            >
                <IconComp
                    v-if="showIcon"
                    :icon="getIcon"
                    class="me-2"
                >
                </IconComp>
            </div>
            <div :class="innerclass">
                <h6
                    v-if="title != null && title != undefined"
                    class="mb-3 fs-5 flex-fill"
                >
                    {{ $tm(props.title!) }}
                </h6>
                <hr v-if="title != null && title != undefined" />
                <slot></slot>
            </div>
            <ButtonComp
                v-if="props.dismissable || (props.dismisskey && props.dismisskey.length > 0)"
                button-type="Custom"
                icon="fa-xmark"
                :class="{
                    'btn-sm': props.size === 'xs' || props.size === 'sm',
                    'text-white': props.fulldismissButton,
                    [`btn-outline-${props.type}`]: props.fulldismissButton,
                }"
                class="position-absolute end-0 top-0 no-focus"
                data-bs-dismiss="alert"
                aria-label="Close"
                >{{ props.fulldismissButton ? $tm('button.dismiss') : '' }}</ButtonComp
            >
        </div>
    </div>
</template>
